/**
 * Table jobs: a Prism-generated main program that runs sub programs at table cells.
 *
 * A placement is a zero offset plus a subroutine call, which this control already does today
 * (P000110 and P001012 set `(ORGX56=…)` then `G56`; every Kuhn program calls `(PCALL 100…)`).
 * Each sub program is written for its home cell; moving it to another cell is a whole number of
 * cells times the table pitch in machine X and Y. Nothing here contacts the machine.
 */

export interface MotionmasterJobPlacement {
  subProgramId: string;
  /** The exact sub version the job was generated against; the job must be regenerated when it changes. */
  subVersionId: string;
  digest: string;
  /** 0-based column along +machine X, 1-based row along +machine Y. */
  column: number;
  row: number;
  /** Machining order, 1 first. */
  sequence: number;
}

export interface MotionmasterJobLayout {
  pitch: number;
  /** The zero offset the job sets for each placement, 54-59. */
  offsetCode: number;
  placements: MotionmasterJobPlacement[];
}

/** What the generator needs to know about a sub program beyond the placement. */
export interface MotionmasterJobSub {
  programNumber: number;
  name: string;
  home: { column: number; row: number };
}

export const MOTIONMASTER_JOB_PARK = 'G0 X44.9796 Y120 Z0 U0 V0';
/** Header blocks the Kuhn programs share; a job issues them once and the sub form drops them. */
const JOB_HEADER_BLOCKS = ['S10000 M3', 'G5', 'G4 K300'];

export function emptyMotionmasterJobLayout(): MotionmasterJobLayout {
  return { pitch: 10, offsetCode: 56, placements: [] };
}

export function parseMotionmasterJobLayout(raw: string | null | undefined): MotionmasterJobLayout {
  const fallback = emptyMotionmasterJobLayout();
  if (!raw) return fallback;
  try {
    const parsed = JSON.parse(raw) as Partial<MotionmasterJobLayout>;
    const placements = Array.isArray(parsed.placements) ? parsed.placements : [];
    return {
      pitch: Number.isFinite(parsed.pitch) && (parsed.pitch as number) > 0 ? (parsed.pitch as number) : fallback.pitch,
      offsetCode: Number.isInteger(parsed.offsetCode) && (parsed.offsetCode as number) >= 54 && (parsed.offsetCode as number) <= 59
        ? (parsed.offsetCode as number) : fallback.offsetCode,
      placements: placements
        .filter(placement => placement && typeof placement.subProgramId === 'string' && Number.isInteger(placement.column) && Number.isInteger(placement.row))
        .map((placement, index) => ({
          subProgramId: placement.subProgramId,
          subVersionId: String(placement.subVersionId ?? ''),
          digest: String(placement.digest ?? ''),
          column: placement.column,
          row: placement.row,
          sequence: Number.isInteger(placement.sequence) ? placement.sequence : index + 1,
        }))
        .sort((a, b) => a.sequence - b.sequence),
    };
  } catch {
    return fallback;
  }
}

export function serializeMotionmasterJobLayout(layout: MotionmasterJobLayout): string {
  return JSON.stringify({
    pitch: layout.pitch,
    offsetCode: layout.offsetCode,
    placements: [...layout.placements].sort((a, b) => a.sequence - b.sequence),
  });
}

export function motionmasterCellLabel(column: number, row: number): string {
  return `${String.fromCharCode(65 + column)}${row}`;
}

const body = (line: string): string => line.split(';', 1)[0].trim();

/**
 * The subroutine form of a part program: the same blocks between `(SUB n)` and `(RET)`, minus the
 * `%` line, the shared spindle/G5/dwell header and M30. Park moves stay inside so the sub still ends
 * clear of the part; under a zero offset they shift with the fixture, which keeps them above it.
 */
export function motionmasterSubroutineText(programNumber: number, text: string): string {
  const lines = text.replace(/\r\n/g, '\n').replace(/\n$/, '').split('\n');
  const source = lines[0]?.startsWith('%') ? lines.slice(1) : lines;
  const header: string[] = [];
  let index = 0;
  while (index < source.length && (source[index].trim() === '' || source[index].trim().startsWith(';'))) {
    header.push(source[index]);
    index++;
  }
  const rest: string[] = [];
  for (const line of source.slice(index)) {
    const code = body(line);
    if (/^M30\b/i.test(code)) break;
    // the spindle runs for the whole job: no M5 between placements
    if (/^M5\b/i.test(code)) continue;
    if (JOB_HEADER_BLOCKS.some(block => code.replace(/\s+/g, ' ') === block)) continue;
    if (/^\(\s*(SUB|RET)\b/i.test(code)) continue;
    rest.push(line);
  }
  const name = (lines[0] ?? '').replace(/^%/, '').split(',')[0].trim();
  return [
    `%${name} SUB,MX,`,
    `;SUBROUTINE FORM OF ${name || 'P' + programNumber} FOR TABLE JOBS - RUN WITH (CALL ${programNumber}) UNDER A ZERO OFFSET`,
    `;SPINDLE ${JOB_HEADER_BLOCKS[0]}, ${JOB_HEADER_BLOCKS[1]}, ${JOB_HEADER_BLOCKS[2]} AND M5 ARE ISSUED BY THE JOB`,
    ...header,
    `(SUB ${programNumber})`,
    ...rest,
    '(RET)',
  ].join('\n') + '\n';
}

/** The lines between `(SUB n)` and `(RET)` of a program, or null when it holds no such subroutine. */
export function motionmasterSubroutineBody(text: string, programNumber: number): string[] | null {
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  const start = lines.findIndex(line => new RegExp(`^\\(\\s*SUB\\s*0*${programNumber}\\s*\\)`, 'i').test(body(line)));
  if (start < 0) return null;
  const rest = lines.slice(start + 1);
  const end = rest.findIndex(line => /^\(\s*RET\s*\)/i.test(body(line)));
  return end < 0 ? rest : rest.slice(0, end);
}

const formatInches = (value: number): string => {
  const rounded = Math.round(value * 10000) / 10000;
  return Math.abs(rounded) < 1e-9 ? '0' : rounded.toFixed(4).replace(/\.?0+$/, '');
};

/** Why a layout cannot be generated, in the order a person should fix them; empty when it can. */
export function motionmasterJobIssues(layout: MotionmasterJobLayout, subsByProgramId: Record<string, MotionmasterJobSub | undefined>): string[] {
  const issues: string[] = [];
  if (!layout.placements.length) issues.push('place at least one sub program on the table');
  if (!(layout.pitch > 0)) issues.push('the table pitch must be positive');
  const seen = new Map<string, number>();
  for (const placement of layout.placements) {
    const sub = subsByProgramId[placement.subProgramId];
    if (!sub) issues.push(`placement ${placement.sequence}: its sub program is missing or has no home cell`);
    if (!placement.subVersionId || !placement.digest) issues.push(`placement ${placement.sequence}: no sub version pinned`);
    const cell = motionmasterCellLabel(placement.column, placement.row);
    const other = seen.get(cell);
    if (other !== undefined) issues.push(`placements ${other} and ${placement.sequence} both sit on ${cell}`);
    seen.set(cell, placement.sequence);
  }
  return issues;
}

/**
 * The job's program text. Per placement: set the zero offset to the whole-cell move from the sub's
 * home cell, select it, call the sub, then zero the offset and park in machine coordinates.
 */
export function buildMotionmasterJobText(job: {
  name: string;
  versionId: string;
  layout: MotionmasterJobLayout;
  subsByProgramId: Record<string, MotionmasterJobSub | undefined>;
}): string {
  const issues = motionmasterJobIssues(job.layout, job.subsByProgramId);
  if (issues.length) throw new Error(issues[0]);
  const code = job.layout.offsetCode;
  const zero = `(ORGX${code}=0,ORGY${code}=0,ORGZ${code}=0)`;
  const placements = [...job.layout.placements].sort((a, b) => a.sequence - b.sequence);
  const subs = placements.map(placement => job.subsByProgramId[placement.subProgramId] as MotionmasterJobSub);
  const lines: string[] = [
    `%${job.name.toUpperCase().replace(/,/g, ' ')},MX,`,
    `;PRISM TABLE JOB ${job.versionId} - ${placements.length} PLACEMENT${placements.length === 1 ? '' : 'S'} - PITCH ${formatInches(job.layout.pitch)} IN`,
    ';CELL LETTERS RUN WITH +X, NUMBERS WITH +Y. EACH OFFSET IS WHOLE CELLS FROM THE SUB PROGRAM HOME CELL',
    `;SUB PROGRAMS MUST BE ON THE CONTROL: ${[...new Set(subs.map(sub => `P${String(sub.programNumber).padStart(6, '0')}`))].join(', ')}`,
    ';NO HEAD, TABLE OR NEIGHBOURING FIXTURE COLLISION CHECK',
    ...JOB_HEADER_BLOCKS,
    'G70 G90 G17',
    zero,
    `G${code}`,
    MOTIONMASTER_JOB_PARK,
  ];
  placements.forEach((placement, index) => {
    const sub = subs[index];
    const dx = (placement.column - sub.home.column) * job.layout.pitch;
    const dy = (placement.row - sub.home.row) * job.layout.pitch;
    lines.push(
      `;${placement.sequence} ${sub.name} P${String(sub.programNumber).padStart(6, '0')} AT ${motionmasterCellLabel(placement.column, placement.row)} (HOME ${motionmasterCellLabel(sub.home.column, sub.home.row)}) MOVE X${formatInches(dx)} Y${formatInches(dy)}`,
      `(ORGX${code}=${formatInches(dx)},ORGY${code}=${formatInches(dy)},ORGZ${code}=0)`,
      `G${code}`,
      `(CALL ${sub.programNumber})`,
      zero,
      `G${code}`,
      MOTIONMASTER_JOB_PARK,
    );
  });
  lines.push('M5', 'M30');
  return lines.join('\n') + '\n';
}
